<template>
  <BaseLayout>
    <div class="space-y-6">
      <!-- Header -->
      <div class="flex justify-between items-center">
        <div>
          <h1 class="text-2xl font-semibold text-gray-900">Gestión de Usuarios</h1>
          <p class="mt-1 text-sm text-gray-600">
            Lista de todos los usuarios del sistema con sus roles y estados.
          </p>
        </div>
        <div class="flex space-x-3">
          <button class="btn-secondary">
            Importar CSV
          </button>
          <router-link to="/users/create" class="btn-primary">
            <PlusIcon class="h-4 w-4 mr-2" />
            Nuevo Usuario
          </router-link>
        </div>
      </div>

      <!-- Filters -->
      <div class="bg-white shadow rounded-lg p-6">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label class="form-label">Buscar</label>
            <input
              v-model="filters.search"
              type="text"
              class="form-input"
              placeholder="Nombre, email o usuario..."
              @input="debouncedSearch"
            />
          </div>
          <div>
            <label class="form-label">Rol</label>
            <select v-model="filters.role" class="form-input" @change="loadUsers">
              <option value="">Todos los roles</option>
              <option value="admin">Admin</option>
              <option value="VALIDACION">Validación</option>
              <option value="DIGITACION">Digitación</option>
              <option value="SUPERVISION">Supervisión</option>
              <option value="TRANSMISION">Transmisión</option>
            </select>
          </div>
          <div>
            <label class="form-label">Estado</label>
            <select v-model="filters.enabled" class="form-input" @change="loadUsers">
              <option value="">Todos</option>
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
            </select>
          </div>
          <div>
            <label class="form-label">Por página</label>
            <select v-model="pagination.limit" class="form-input" @change="loadUsers">
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Users Table -->
      <div class="bg-white shadow overflow-hidden sm:rounded-md">
        <div v-if="loading" class="p-8 text-center">
          <div class="loading-spinner mx-auto"></div>
          <p class="mt-2 text-sm text-gray-500">Cargando usuarios...</p>
        </div>

        <div v-else-if="users.length === 0" class="p-8 text-center">
          <UsersIcon class="mx-auto h-12 w-12 text-gray-400" />
          <h3 class="mt-2 text-sm font-medium text-gray-900">No hay usuarios</h3>
          <p class="mt-1 text-sm text-gray-500">
            No se encontraron usuarios con los filtros aplicados.
          </p>
        </div>

        <ul v-else class="divide-y divide-gray-200">
          <li v-for="user in users" :key="user._id" class="px-6 py-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div class="flex-shrink-0 h-10 w-10">
                  <div class="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span class="text-sm font-medium text-primary-600">
                      {{ getUserInitials(user) }}
                    </span>
                  </div>
                </div>
                <div class="ml-4">
                  <div class="flex items-center">
                    <p class="text-sm font-medium text-gray-900">
                      {{ getUserDisplayName(user) }}
                    </p>
                    <span
                      class="ml-2 badge"
                      :class="user.enabled ? 'badge-success' : 'badge-danger'"
                    >
                      {{ user.enabled ? 'Activo' : 'Inactivo' }}
                    </span>
                  </div>
                  <div class="flex items-center mt-1">
                    <p class="text-sm text-gray-500">{{ user.email }}</p>
                    <span class="mx-2 text-gray-300">•</span>
                    <p class="text-sm text-gray-500">@{{ user.username }}</p>
                  </div>
                </div>
              </div>
              
              <div class="flex items-center space-x-4">
                <div class="text-right">
                  <span
                    class="badge"
                    :class="getRoleBadgeClass(user.role)"
                  >
                    {{ getRoleDisplayName(user.role) }}
                  </span>
                  <p v-if="user.establecimiento" class="text-xs text-gray-500 mt-1">
                    {{ user.establecimiento }}
                  </p>
                </div>
                
                <div class="flex items-center space-x-2">
                  <router-link
                    :to="`/users/${user._id}/edit`"
                    class="text-primary-600 hover:text-primary-500"
                  >
                    <PencilIcon class="h-4 w-4" />
                  </router-link>
                  <button
                    @click="confirmDelete(user)"
                    class="text-red-600 hover:text-red-500"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.pages > 1" class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div class="flex-1 flex justify-between sm:hidden">
          <button
            @click="changePage(pagination.page - 1)"
            :disabled="pagination.page <= 1"
            class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            @click="changePage(pagination.page + 1)"
            :disabled="pagination.page >= pagination.pages"
            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p class="text-sm text-gray-700">
              Mostrando
              <span class="font-medium">{{ (pagination.page - 1) * pagination.limit + 1 }}</span>
              a
              <span class="font-medium">{{ Math.min(pagination.page * pagination.limit, pagination.total) }}</span>
              de
              <span class="font-medium">{{ pagination.total }}</span>
              resultados
            </p>
          </div>
          <div>
            <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                @click="changePage(pagination.page - 1)"
                :disabled="pagination.page <= 1"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeftIcon class="h-5 w-5" />
              </button>
              
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="changePage(page)"
                :class="[
                  page === pagination.page
                    ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                ]"
              >
                {{ page }}
              </button>
              
              <button
                @click="changePage(pagination.page + 1)"
                :disabled="pagination.page >= pagination.pages"
                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRightIcon class="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showDeleteModal = false"></div>
        
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <ExclamationTriangleIcon class="h-6 w-6 text-red-600" />
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  Eliminar Usuario
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    ¿Estás seguro de que quieres eliminar al usuario <strong>{{ userToDelete?.username }}</strong>?
                    Esta acción no se puede deshacer y eliminará el usuario tanto del sistema local como de Keycloak.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              @click="deleteUser"
              :disabled="deleting"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              <span v-if="!deleting">Eliminar</span>
              <span v-else class="flex items-center">
                <div class="loading-spinner mr-2"></div>
                Eliminando...
              </span>
            </button>
            <button
              @click="showDeleteModal = false"
              :disabled="deleting"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  </BaseLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { apiService } from '@/services/apiService'
import { useToast } from 'vue-toastification'
import BaseLayout from '@/components/BaseLayout.vue'
import {
  PlusIcon,
  UsersIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'

const toast = useToast()

const loading = ref(false)
const deleting = ref(false)
const users = ref([])
const showDeleteModal = ref(false)
const userToDelete = ref(null)

const filters = reactive({
  search: '',
  role: '',
  enabled: ''
})

const pagination = reactive({
  page: 1,
  limit: 20,
  total: 0,
  pages: 0
})

let searchTimeout: ReturnType<typeof setTimeout> | null = null

const visiblePages = computed(() => {
  const pages = []
  const start = Math.max(1, pagination.page - 2)
  const end = Math.min(pagination.pages, pagination.page + 2)
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})

function debouncedSearch() {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  
  searchTimeout = setTimeout(() => {
    pagination.page = 1
    loadUsers()
  }, 500)
}

async function loadUsers() {
  try {
    loading.value = true
    
    const params: any = {
      page: pagination.page,
      limit: pagination.limit
    }
    
    if (filters.search) params.search = filters.search
    if (filters.role) params.role = filters.role
    
    const response = await apiService.getUsers(params) as any
    
    users.value = response.users
    pagination.total = response.pagination.total
    pagination.pages = response.pagination.pages
    
  } catch (error: any) {
    toast.error('Error cargando usuarios: ' + error.message)
  } finally {
    loading.value = false
  }
}

function changePage(page: number) {
  if (page >= 1 && page <= pagination.pages) {
    pagination.page = page
    loadUsers()
  }
}

function getRoleDisplayName(role: string): string {
  const roleNames: Record<string, string> = {
    'admin': 'Administrador',
    'VALIDACION': 'Validador',
    'DIGITACION': 'Digitador',
    'SUPERVISION': 'Supervisor',
    'TRANSMISION': 'Transmisor'
  }
  return roleNames[role] || role
}

function getRoleBadgeClass(role: string): string {
  const roleClasses: Record<string, string> = {
    'admin': 'badge-danger',
    'VALIDACION': 'badge-info',
    'DIGITACION': 'badge-warning',
    'SUPERVISION': 'badge-success',
    'TRANSMISION': 'badge-info'
  }
  return roleClasses[role] || 'badge-info'
}

function getUserInitials(user: any): string {
  if (user.nombre) {
    // Si tiene nombre, usar las primeras dos letras del nombre
    return user.nombre.substring(0, 2).toUpperCase()
  } else if (user.username) {
    // Si no tiene nombre, usar las primeras dos letras del username
    return user.username.substring(0, 2).toUpperCase()
  }
  return 'U'
}

function getUserDisplayName(user: any): string {
  return user.nombre || user.username || 'Usuario'
}

function confirmDelete(user: any) {
  userToDelete.value = user
  showDeleteModal.value = true
}

async function deleteUser() {
  if (!userToDelete.value) return
  
  try {
    deleting.value = true
    
    await apiService.deleteUser(userToDelete.value._id)
    
    toast.success(`Usuario ${userToDelete.value.username} eliminado exitosamente`)
    
    showDeleteModal.value = false
    userToDelete.value = null
    
    // Recargar usuarios
    await loadUsers()
    
  } catch (error: any) {
    toast.error('Error eliminando usuario: ' + error.message)
  } finally {
    deleting.value = false
  }
}

onMounted(() => {
  loadUsers()
})
</script>
